/**
 * Bounded-concurrency upload queue with progress and a remaining-time estimate.
 *
 * What this replaces: both upload screens used to either fire every file at once
 * (`Promise.allSettled` over the whole list) or walk them strictly one at a time.
 * The first stampedes the backend and queues behind the browser's six-connection
 * limit with no feedback; the second multiplies the per-file SharePoint latency by
 * the number of files. A fixed worker pool sits between the two: a handful of
 * uploads stay in flight, each file is compressed just before its turn, and the
 * user sees exactly where the batch is.
 *
 * Progress bookkeeping lives in refs and is snapshotted into React state on a
 * timer. XHR fires progress events far more often than is worth re-rendering for,
 * and with 50 files in a batch, setState-per-event would dominate the main thread.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import {
  UploadAbortedError,
  UploadHttpError,
  UploadNetworkError,
  uploadWithProgress,
} from "shared/api/upload-with-progress";
import {
  compressImageFile,
  describeCompression,
} from "shared/utils/imageCompression";
import type {
  UploadBatchResult,
  UploadQueueOptions,
  UploadQueueSummary,
  UploadTaskInput,
  UploadTaskState,
  UploadTaskStatus,
} from "./uploadQueue.types";

const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_ATTEMPTS = 3;

/** How often the ref-held progress is published to React. */
const UI_TICK_MS = 150;

/**
 * Fallback for the server-side portion of a single upload (SharePoint write plus
 * database work) before we have measured a real one. Only used for the first few
 * seconds of a batch, after which the measured average takes over.
 */
const ASSUMED_SERVER_MS = 2500;

/** Smoothing factor for the throughput estimate; higher reacts faster. */
const THROUGHPUT_SMOOTHING = 0.3;

interface MutableTask {
  input: UploadTaskInput;
  state: UploadTaskState;
  bytesSent: number;
  bytesTotal: number;
  originalSize: number;
  effectiveSize: number;
  compressionNote: string | null;
  attempts: number;
  errorMessage: string | null;
  isRetryable: boolean;
  response: unknown;
  /** Timestamp the last byte left the browser, for measuring server time. */
  uploadDoneAt: number | null;
  serverMs: number | null;
}

const TERMINAL_STATES: UploadTaskState[] = ["success", "error", "canceled"];
const isTerminal = (state: UploadTaskState) => TERMINAL_STATES.includes(state);

const createMutableTask = (input: UploadTaskInput): MutableTask => ({
  input,
  state: "pending",
  bytesSent: 0,
  bytesTotal: input.file.size,
  originalSize: input.file.size,
  effectiveSize: input.file.size,
  compressionNote: null,
  attempts: 0,
  errorMessage: null,
  isRetryable: false,
  response: null,
  uploadDoneAt: null,
  serverMs: null,
});

const toStatus = (task: MutableTask): UploadTaskStatus => ({
  id: task.input.id,
  displayName: task.input.displayName || task.input.file.name,
  state: task.state,
  fraction:
    task.state === "success"
      ? 1
      : task.bytesTotal > 0
        ? Math.min(1, task.bytesSent / task.bytesTotal)
        : 0,
  bytesSent: task.bytesSent,
  bytesTotal: task.bytesTotal,
  originalSize: task.originalSize,
  effectiveSize: task.effectiveSize,
  compressionNote: task.compressionNote,
  attempts: task.attempts,
  errorMessage: task.errorMessage,
  isRetryable: task.isRetryable,
  response: task.response,
  meta: task.input.meta,
});

const describeError = (error: unknown): string => {
  if (error instanceof UploadHttpError) {
    // The backend returns Dutch messages for validation failures; surface them
    // rather than a generic status line.
    const body = error.responseText?.trim();
    if (body) {
      try {
        const parsed = JSON.parse(body);
        if (parsed?.message) return String(parsed.message);
      } catch {
        // Not JSON; fall through to the raw text.
      }
      return body.slice(0, 300);
    }
    return `Server antwoordde met status ${error.status}`;
  }
  if (error instanceof UploadNetworkError) {
    return "Geen verbinding met de server. Controleer je internetverbinding.";
  }
  if (error instanceof UploadAbortedError) return "Geannuleerd";
  if (error instanceof Error) return error.message;
  return "Onbekende fout tijdens upload";
};

const isRetryableError = (error: unknown): boolean => {
  if (error instanceof UploadAbortedError) return false;
  if (error instanceof UploadNetworkError) return true;
  if (error instanceof UploadHttpError) return error.isRetryable;
  return false;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadAbortedError("wachttijd"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new UploadAbortedError("wachttijd"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });

const EMPTY_SUMMARY: UploadQueueSummary = {
  isRunning: false,
  total: 0,
  completed: 0,
  succeeded: 0,
  failed: 0,
  canceled: 0,
  bytesTotal: 0,
  bytesSent: 0,
  fraction: 0,
  throughputBytesPerSecond: null,
  etaMs: null,
  bytesSavedByCompression: 0,
};

export const useUploadQueue = (options: UploadQueueOptions) => {
  const {
    session,
    url,
    concurrency = DEFAULT_CONCURRENCY,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    compression,
    onTaskSuccess,
    onTaskError,
    onBatchComplete,
  } = options;

  const [tasks, setTasks] = useState<UploadTaskStatus[]>([]);
  const [summary, setSummary] = useState<UploadQueueSummary>(EMPTY_SUMMARY);

  const tasksRef = useRef<MutableTask[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const isRunningRef = useRef(false);
  const isMountedRef = useRef(true);

  /** Throughput sampling: bytes seen since the last sample, and when that was. */
  const throughputRef = useRef({
    smoothedBytesPerSecond: null as number | null,
    windowBytes: 0,
    windowStartedAt: 0,
  });

  /** Measured server-side durations, used for the ETA. */
  const serverTimingRef = useRef({ totalMs: 0, samples: 0 });

  // Keep the latest callbacks reachable from the long-lived worker loop without
  // making `start` depend on them (which would recreate it on every render).
  const callbacksRef = useRef({ onTaskSuccess, onTaskError, onBatchComplete });
  useEffect(() => {
    callbacksRef.current = { onTaskSuccess, onTaskError, onBatchComplete };
  }, [onTaskSuccess, onTaskError, onBatchComplete]);

  useEffect(() => {
    // Set on every setup, not just the first. StrictMode runs effects as
    // setup -> cleanup -> setup, so without this the cleanup's `false` was never
    // undone: `publish` then skipped every update, `tasks` stayed empty and the
    // progress panel (which renders nothing for an empty list) never appeared.
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Leaving the page must not leave XHRs running against a dead component.
      abortRef.current?.abort();
    };
  }, []);

  const computeSummary = useCallback((): UploadQueueSummary => {
    const all = tasksRef.current;
    const total = all.length;

    let bytesTotal = 0;
    let bytesSent = 0;
    let succeeded = 0;
    let failed = 0;
    let canceled = 0;
    let completed = 0;
    let bytesSavedByCompression = 0;
    let unfinished = 0;

    for (const task of all) {
      bytesTotal += task.bytesTotal;
      // A finished file counts as fully sent even if the last progress event was
      // never delivered, so the bar can actually reach 100%.
      bytesSent += task.state === "success" ? task.bytesTotal : task.bytesSent;
      bytesSavedByCompression += Math.max(
        0,
        task.originalSize - task.effectiveSize,
      );

      if (task.state === "success") succeeded++;
      else if (task.state === "error") failed++;
      else if (task.state === "canceled") canceled++;

      if (isTerminal(task.state)) completed++;
      else unfinished++;
    }

    const throughput = throughputRef.current.smoothedBytesPerSecond;
    const { totalMs, samples } = serverTimingRef.current;
    const avgServerMs = samples > 0 ? totalMs / samples : ASSUMED_SERVER_MS;

    let etaMs: number | null = null;
    if (unfinished === 0) {
      etaMs = 0;
    } else if (throughput && throughput > 0) {
      // Wall-clock for a worker pool ≈ time to push the remaining bytes at the
      // observed aggregate speed, plus the server time each remaining file still
      // costs, divided across the workers that will absorb it in parallel.
      const remainingBytes = Math.max(0, bytesTotal - bytesSent);
      const transferMs = (remainingBytes / throughput) * 1000;
      const serverMs =
        (unfinished / Math.max(1, concurrency)) * avgServerMs;
      etaMs = transferMs + serverMs;
    }

    return {
      isRunning: isRunningRef.current,
      total,
      completed,
      succeeded,
      failed,
      canceled,
      bytesTotal,
      bytesSent,
      fraction: bytesTotal > 0 ? Math.min(1, bytesSent / bytesTotal) : 0,
      throughputBytesPerSecond: throughput,
      etaMs,
      bytesSavedByCompression,
    };
  }, [concurrency]);

  const publish = useCallback(() => {
    if (!isMountedRef.current) return;
    setTasks(tasksRef.current.map(toStatus));
    setSummary(computeSummary());
  }, [computeSummary]);

  /**
   * Samples transfer speed. Only closes a window that actually saw bytes, so the
   * gaps while workers wait on the server do not drag the estimate down.
   */
  const sampleThroughput = useCallback(() => {
    const state = throughputRef.current;
    const now = Date.now();
    if (state.windowStartedAt === 0) {
      state.windowStartedAt = now;
      return;
    }
    const elapsed = now - state.windowStartedAt;
    if (elapsed < 400) return;

    if (state.windowBytes > 0) {
      const sample = (state.windowBytes / elapsed) * 1000;
      state.smoothedBytesPerSecond =
        state.smoothedBytesPerSecond === null
          ? sample
          : state.smoothedBytesPerSecond * (1 - THROUGHPUT_SMOOTHING) +
            sample * THROUGHPUT_SMOOTHING;
    }

    state.windowBytes = 0;
    state.windowStartedAt = now;
  }, []);

  /** Runs one task through compression, upload and retries. */
  const runTask = useCallback(
    async (task: MutableTask, signal: AbortSignal) => {
      let fileToUpload = task.input.file;

      // --- Compression -------------------------------------------------------
      if (compression?.enabled !== false) {
        task.state = "compressing";
        try {
          const result = await compressImageFile(task.input.file, compression);
          fileToUpload = result.file;
          task.effectiveSize = result.compressedSize;
          task.bytesTotal = result.compressedSize;
          task.compressionNote = describeCompression(result);
        } catch (error) {
          // compressImageFile swallows its own failures, so this is unexpected.
          Sentry.captureException(error, {
            extra: {
              context: "useUploadQueue.compress",
              fileName: task.input.file.name,
            },
          });
        }
      }

      if (signal.aborted) {
        task.state = "canceled";
        task.errorMessage = "Geannuleerd";
        return;
      }

      // --- Upload with retries ----------------------------------------------
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        task.attempts = attempt;
        task.state = "uploading";
        task.bytesSent = 0;
        task.uploadDoneAt = null;
        task.errorMessage = null;

        try {
          const body = await task.input.buildBody(fileToUpload);

          const response = await uploadWithProgress({
            url,
            body,
            session,
            signal,
            onProgress: ({ loaded }) => {
              const delta = Math.max(0, loaded - task.bytesSent);
              task.bytesSent = loaded;
              throughputRef.current.windowBytes += delta;
            },
            onUploadComplete: () => {
              // Bytes are out; whatever comes next is the server's time.
              task.bytesSent = task.bytesTotal;
              task.state = "processing";
              task.uploadDoneAt = Date.now();
            },
          });

          if (task.uploadDoneAt) {
            task.serverMs = Date.now() - task.uploadDoneAt;
            serverTimingRef.current.totalMs += task.serverMs;
            serverTimingRef.current.samples += 1;
          }

          task.state = "success";
          task.response = response;
          task.bytesSent = task.bytesTotal;
          callbacksRef.current.onTaskSuccess?.(toStatus(task));
          return;
        } catch (error) {
          if (error instanceof UploadAbortedError || signal.aborted) {
            task.state = "canceled";
            task.errorMessage = "Geannuleerd";
            return;
          }

          const retryable = isRetryableError(error);
          const hasAttemptsLeft = attempt < maxAttempts;

          if (retryable && hasAttemptsLeft) {
            // Exponential backoff with jitter, so a batch that hits a rate limit
            // does not retry in lockstep.
            const backoffMs = Math.min(
              5000,
              500 * 2 ** (attempt - 1) + Math.random() * 250,
            );
            task.state = "pending";
            task.errorMessage = `Poging ${attempt} mislukt, opnieuw proberen...`;
            try {
              await sleep(backoffMs, signal);
            } catch {
              task.state = "canceled";
              task.errorMessage = "Geannuleerd";
              return;
            }
            continue;
          }

          task.state = "error";
          task.errorMessage = describeError(error);
          task.isRetryable = retryable;
          callbacksRef.current.onTaskError?.(toStatus(task));
          return;
        }
      }
    },
    [compression, maxAttempts, session, url],
  );

  /**
   * Fixed-size worker pool over a shared cursor. JavaScript's single-threaded
   * event loop makes the cursor increment safe without any locking.
   */
  const runPool = useCallback(
    async (queue: MutableTask[], signal: AbortSignal) => {
      let cursor = 0;
      const workerCount = Math.max(1, Math.min(concurrency, queue.length));

      const worker = async () => {
        while (!signal.aborted) {
          const task = queue[cursor++];
          if (!task) return;
          await runTask(task, signal);
        }
        // Aborted mid-flight: mark whatever never started as canceled so the UI
        // does not show files stuck on "wachten".
        for (let i = cursor; i < queue.length; i++) {
          if (!isTerminal(queue[i].state)) {
            queue[i].state = "canceled";
            queue[i].errorMessage = "Geannuleerd";
          }
        }
      };

      await Promise.all(Array.from({ length: workerCount }, worker));
    },
    [concurrency, runTask],
  );

  const start = useCallback(
    async (inputs: UploadTaskInput[]): Promise<UploadBatchResult> => {
      if (isRunningRef.current) {
        throw new Error("Er loopt al een upload; wacht tot deze klaar is.");
      }

      const queue = inputs.map(createMutableTask);
      tasksRef.current = queue;
      abortRef.current = new AbortController();
      isRunningRef.current = true;
      throughputRef.current = {
        smoothedBytesPerSecond: null,
        windowBytes: 0,
        windowStartedAt: Date.now(),
      };
      serverTimingRef.current = { totalMs: 0, samples: 0 };
      publish();

      const ticker = setInterval(() => {
        sampleThroughput();
        publish();
      }, UI_TICK_MS);

      try {
        await runPool(queue, abortRef.current.signal);
      } finally {
        clearInterval(ticker);
        isRunningRef.current = false;
        publish();
      }

      const finalTasks = queue.map(toStatus);
      const result: UploadBatchResult = {
        tasks: finalTasks,
        succeeded: finalTasks.filter((t) => t.state === "success"),
        failed: finalTasks.filter((t) => t.state === "error"),
        canceled: finalTasks.filter((t) => t.state === "canceled"),
      };

      callbacksRef.current.onBatchComplete?.(finalTasks);
      return result;
    },
    [publish, runPool, sampleThroughput],
  );

  const cancelAll = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** Clears the panel; safe to call only when nothing is running. */
  const reset = useCallback(() => {
    if (isRunningRef.current) return;
    tasksRef.current = [];
    setTasks([]);
    setSummary(EMPTY_SUMMARY);
  }, []);

  const hasActiveBatch = useMemo(
    () => tasks.length > 0 && (summary.isRunning || summary.completed > 0),
    [tasks.length, summary.isRunning, summary.completed],
  );

  return { tasks, summary, hasActiveBatch, start, cancelAll, reset };
};

export type UseUploadQueueReturn = ReturnType<typeof useUploadQueue>;
